import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CourseSummaryComponent, LoadingCoursePage } from "../../components/course/view"
import { useSelector } from "react-redux";
import { LearningRecordCourse, UpdateCourseContentSessionRequest } from "src/types/coaching/learners";
import React, { useEffect, useMemo } from "react";
import { CourseContext, CourseContextProps } from "../../components/course/view/context";

import _ from 'lodash';
import cx from 'classnames';

import styles from "./view.module.scss";
import { useDispatch } from "react-redux";
import {
  completeCourseContentSession,
  createCourseContentSession,
  createLessonAnalyticsEvent,
  fetchAssessmentReport,
  fetchCourseContentSession,
  fetchLearningRecordCourse,
  fetchLessonForLearningRecord,
  fetchLessonPreviewForLearningRecord,
  invalidateCourseContentSession,
  invalidateRetakeCourse,
  invalidateStartCourse,
  recordAnswerForQuestion,
  recordLessonAnalyticsEvent,
  recordQuestionSession,
  retakeCourse,
  startCourse,
  updateCourseContentSession
} from "src/redux/actions/coaching/learners";
import { hideSideMenu } from "src/redux/actions/hub";

import { CourseStructureComponent } from "../../components/course/view/structure";
import { Assessment } from "src/types/coaching/courses";

import userRoleService from "src/services/userRoles.service";
import PermissionDenined from "src/components/permissionDenied";
import { CourseNotFound } from "src/pages/coaching/components/notFound";

export const ViewDemoCoursePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();

  const courseId = params.courseId;

  const course = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].item`) as LearningRecordCourse,
  );

  const loading = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].loading`),
  );

  const courseErrorCode = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].error.errors[0].code`);
  });

  const startingCourse = useSelector((state: any) => {
    const starting = _.get(state.learners, `course[${courseId}].start.starting`);
    return starting;
  });

  const courseStarted = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].start.started`);
  });

  const retakingCourse = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].retake.retaking`);
  });

  const courseRetaken = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].retake.retaken`);
  });

  const courseRetakenId = useSelector((state: any) => {
    return _.get(state.learners, `course[${courseId}].retake.id`);
  });

  const [courseContext, setCourseContext] = React.useState<CourseContextProps>({
    id: courseId,
    startAssessment: () => { },
    endAssessment: () => { },
  });

  useEffect(() => {
    dispatch(hideSideMenu(true));

    if (courseId && !course) {
      dispatch(fetchLearningRecordCourse(courseId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: courseId,
        startAssessment: () => { },
        endAssessment: () => { },
      }
    });
  }, []);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        onBack: undefined,
        onContentClicked: handleContentClicked,
        startCourse: handleStartCourse,
        endCourse: handleEndCourse,
        retakeCourse: handleRetakeCourse
      }
    });
  }, [course]);

  useEffect(() => {
    if (courseStarted) {
      navigate(`/coaching/demo/courses/${courseId}/contents?index=0`);
      dispatch(invalidateStartCourse(course.id));
    }
  }, [courseStarted]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        startingCourse: startingCourse
      }
    });
  }, [startingCourse]);
  
  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        retakingCourse: retakingCourse,
      }
    });
  }, [retakingCourse]);

  useEffect(() => {
    if (courseRetaken && courseRetakenId) {
      navigate(`/coaching/demo/courses/${courseRetakenId}/contents?index=0`);
      dispatch(invalidateRetakeCourse(course.id));
    }
  }, [courseRetaken]);

  const handleContentClicked = (index) => {
    if (course.status !== "NOT_STARTED") {
      navigate(`/coaching/demo/courses/${courseId}/contents?index=${index}`);
    }
  }

  const handleStartCourse = () => {
    dispatch(startCourse(course.id));
  }

  const handleEndCourse = () => {
    // dispatch(endCourse(course.id));
  }

  const handleRetakeCourse = () => {
    dispatch(retakeCourse(course.id));
  }

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if (courseErrorCode === 10007) {
    return <CourseNotFound onBack={() => {
      navigate(`/coaching/manage`);
    }} />
  }

  if ((loading && course?.id != courseId) || !course) {
    return (
      <div className={styles.courseContainer}>
        <div className={styles.left}>
          <div className={styles.courseDetails}>
            <LoadingCoursePage />
          </div>
        </div>
      </div>
    );
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <CourseSummaryComponent
        course={course}
      />
    </CourseContext.Provider>
  );
}

export const ViewDemoCourseStructurePage: React.FC<object> = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const courseId = params.id;

  const course = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].item`) as LearningRecordCourse,
  );

  const contentIndex = useMemo(() => {
    return parseInt(searchParams.get("index") || "0");
  }, [searchParams]);

  const courseContent = useMemo(() => {
    return _.get(course, `course.contents[${contentIndex}]`);
  }, [course, contentIndex]);

  const loading = useSelector((state: any) =>
    _.get(state.learners, `course[${courseId}].loading`),
  );

  const courseContentSession = useSelector((state: any) => {
    const sessions = _.get(state.learners, `course[${course?.id}].item.sessions`) || [];
    const item = sessions?.find((item) => item.id === courseContent?.id && item.type === courseContent?.entityType);

    return item;
  }, _.isEqual);

  const { created, fetched, fetchedReport, session } = useMemo(() => {
    const created = _.get(courseContentSession, "created") || false;
    const fetched = _.get(courseContentSession, "fetched") || false;
    const fetchedReport = _.get(courseContentSession, "fetchedReport") || false;

    const session = _.get(courseContentSession, "session");

    return {
      created,
      fetched,
      fetchedReport,
      session,
    }
  }, [courseContentSession]);

  const contents = useMemo(() => course?.course?.contents || [], [course]);

  const asset = useSelector((state: any) => {
    if (courseContent?.entityType !== "lesson") {
      return undefined;
    }

    const assetLoading = _.get(state, `learners.course[${courseId}].lessons['${courseContent.id}'].loading`, true);
    const item = _.get(state, `learners.course[${courseId}].lessons['${courseContent.id}'].item`);

    const previewItem = _.get(state, `learners.course[${courseId}].lessons['${courseContent.id}'].preview.item`);
    const previewLoading = _.get(state, `learners.course[${courseId}].lessons['${courseContent.id}'].preview.loading`);

    return {
      loading: assetLoading,
      item: item,
      preview: previewItem,
    };
  }, _.isEqual);

  const canComplete = useSelector((state: any) => {
    const courseItem = _.get(state.learners, `course[${course?.id}].item`);
    const sessions = _.get(courseItem, 'sessions') || [];

    let canComplete = true;
    contents.forEach((content) => {
      const session = sessions.find((session) => session.id === content.id);
      canComplete = canComplete && session != null;
    });
    return canComplete;
  }, _.isEqual);

  const completingCourse = useSelector((state: any) => {
    const completing = _.get(state.learners, `course[${courseId}].complete.completing`);
    return completing;
  });

  const courseCompleted = useSelector((state: any) => {
    const completed = _.get(state.learners, `course[${courseId}].complete.completed`);
    return completed;
  });

  const [courseContext, setCourseContext] = React.useState<CourseContextProps>({
    id: courseId,
    startAssessment: () => { },
    endAssessment: () => { },
  });

  useEffect(() => {
    dispatch(hideSideMenu(true));
    
    if (courseId && !course) {
      dispatch(fetchLearningRecordCourse(courseId));
    }

    setCourseContext((prev) => {
      return {
        ...prev,
        id: courseId,
        startAssessment: handleStartAssessment,
        endAssessment: handleEndAssessment,
      }
    });
  }, []);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        onBack: handleBackClicked,
        onContentClicked: handleContentClicked,
        startAssessment: handleStartAssessment,
        endAssessment: handleEndAssessment,
      }
    });
  }, [course]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContent: courseContent,
        createCourseContentSession: handleCreateCourseContentSession,
        fetchCourseContentSession: handleFetchCourseContentSession,
        updateCourseContentSession: handleUpdateCourseContentSession,
        completeCourseContentSession: handleCompleteCourseContentSession,
        recordAnswerForQuestion: handleRecordAnswerForQuestion,
        recordQuestionSession: handleRecordQuestionSession,
        fetchAssessmentReport: handleFetchAssessmentReport,
        createLessonAnalyticsEvent: handleCreateLessonAnalyticsEvent,
        recordLessonAnalyticsEvent: handleRecordLessonAnalyticsEvent,
        endAssessment: handleEndAssessment,
        fetchLesson: handleFetchLesson,
        fetchLessonPreview: handleFetchLessonPreview,
      }
    });
  }, [courseContent, session]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        courseContentSession: courseContentSession
      }
    });
  }, [courseContentSession]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        asset: asset
      }
    });
  }, [asset]);

  //Assessment related side effects
  useEffect(() => {
    if (created && session && courseContent?.entityType === "assessment") {
      handleStartAssessment(courseContent as Assessment);
      dispatch(invalidateCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: courseContentSession.id,
        type: "assessment",
      }));
    }
  }, [created, session]);

  useEffect(() => {
    if (fetched && session && courseContent?.entityType === "assessment") {
      handleStartAssessment(courseContent as Assessment);
      dispatch(invalidateCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: courseContentSession.id,
        type: "assessment",
      }));
    }
  }, [fetched, session]);

  useEffect(() => {
    if (fetchedReport && session && courseContent?.entityType === "assessment") {
      handleStartAssessment(courseContent as Assessment);
      dispatch(invalidateCourseContentSession({
        course: course.course!.id!,
        learningRecord: course.id!,
        id: courseContentSession.id,
        type: "assessment",
      }));
    }
  }, [fetchedReport, session]);

  useEffect(() => {
    setCourseContext((prev) => {
      return {
        ...prev,
        canCompleteCourse: canComplete,
        completingCourse: completingCourse,
        completedCourse: courseCompleted,
      }
    });
  }, [completingCourse, courseCompleted, canComplete]);

  const handleBackClicked = () => {
    navigate(`/coaching/demo/courses/${course.id}`);
  }

  const handleContentClicked = (index) => {
    if (course.status !== "NOT_STARTED") {
      navigate(`/coaching/demo/courses/${courseId}/contents?index=${index}`);
    }
  }

  const handleCreateCourseContentSession = (courseContent) => {
    dispatch(createCourseContentSession({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
    }));
  }

  const handleFetchCourseContentSession = (courseContent, session) => {
    dispatch(fetchCourseContentSession({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
    }));
  }

  const handleUpdateCourseContentSession = (courseContent, session, duration, progress) => {
    const request = {
      course: course.course.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
      duration: duration,
      percentCompleted: progress
    } as UpdateCourseContentSessionRequest;

    dispatch(updateCourseContentSession(request));
  }

  const handleCompleteCourseContentSession = (courseContent, session, duration, progress) => {
    const request = {
      course: course.course.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
      duration: duration,
      percentCompleted: progress
    };

    dispatch(completeCourseContentSession(request));
  }

  const handleStartAssessment = (assessment) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        activeAssessment: assessment,
      }
    });
  }

  const handleEndAssessment = (assessment) => {
    setCourseContext((prev) => {
      return {
        ...prev,
        activeAssessment: null,
      }
    });

    dispatch(invalidateCourseContentSession({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: assessment!.id,
      type: "assessment",
    }));
  }

  const handleRecordAnswerForQuestion = (assessment, question, answer) => {
    dispatch(recordAnswerForQuestion({
      course: course.course.id!,
      learningRecord: course.id!,
      assessment: assessment.id,
      question: question,
      answer: answer,
    }));
  }

  const handleRecordQuestionSession = (assessment, question, answer, duration) => {
    const request = {
      course: course.course.id!,
      learningRecord: course.id!,
      assessment: assessment.id,
      session: session,
      question: question,
      answer: answer,
      duration: duration,
    };

    dispatch(recordQuestionSession(request));
  }

  const handleFetchAssessmentReport = (courseContent, session) => {
    dispatch(fetchAssessmentReport({
      course: course.course!.id!,
      learningRecord: course.id!,
      id: courseContent.id,
      type: courseContent.entityType,
      session: session,
    }));
  }

  //Lesson
  const handleCreateLessonAnalyticsEvent = (lesson) => {
    dispatch(createLessonAnalyticsEvent({
      course: course!.course!.id!,
      learningRecord: course!.id!,
      lesson: lesson!.id,
      type: 100,
      eventDate: new Date().getTime(),
    }));
  }

  const handleRecordLessonAnalyticsEvent = (lesson, session, request) => {
    dispatch(recordLessonAnalyticsEvent({
      course: course!.course!.id!,
      learningRecord: course!.id!,
      lesson: lesson!.id,
      session: session,
      ...request,
    }));
  }

  const handleFetchLesson = (lesson) => {
    dispatch(fetchLessonForLearningRecord({
      learningRecord: course.id!,
      lesson: lesson.id,
    }));
  }

  const handleFetchLessonPreview = (lesson) => {
    dispatch(fetchLessonPreviewForLearningRecord({
      learningRecord: course.id!,
      lesson: lesson.id,
      includeText: true,
    }));
  }

  if (!userRoleService.canAccessCoaching()) {
    return <PermissionDenined className={styles.permissionDenied} />;
  }

  if ((loading && course?.id != courseId) || !course) {
    return <div className={styles.loading}>Loading course...</div>
  }

  return (
    <CourseContext.Provider value={courseContext}>
      <CourseStructureComponent course={course} />
    </CourseContext.Provider>
  );
}